// Forum & Lex is the platform. Everything a firm's clients, leads and outside
// parties see carries that firm's own identity (core_firm_profile). The firm whose
// name already is the platform brand (the home firm) keeps today's look; every
// other firm's client-facing pages and emails add a small "Powered by" line.
export const PLATFORM_NAME = 'Forum & Lex';
export const POWERED_BY_PLATFORM = `Powered by ${PLATFORM_NAME}`;

function normalizedBrand(name: string | null | undefined): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/\s+and\s+/g, ' & ')
    .replace(/\s+/g, ' ')
    .trim();
}

// True when the firm's own name is the platform brand, e.g. "Forum & Lex" or
// "Forum & Lex P.C.".
export function isPlatformBrand(firmName: string | null | undefined): boolean {
  const name = normalizedBrand(firmName);
  return name === 'forum & lex' || /^forum & lex[\s,.]/.test(name);
}

// A known firm that is not the platform brand shows "Powered by Forum & Lex".
export function showsPoweredByPlatform(firmName: string | null | undefined): boolean {
  return Boolean(firmName?.trim()) && !isPlatformBrand(firmName);
}

// The name staff see as the app's own: the firm they work in, or the platform
// while that firm or its name is unknown.
export function workspaceBrandName(firmName: string | null | undefined): string {
  return firmName?.trim() || PLATFORM_NAME;
}

// Up to two initials for a firm mark when the firm has no logo.
export function firmInitials(firmName: string | null | undefined): string {
  const words = (firmName ?? '')
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

// A firm website as printed on documents: "www.smithlaw.com" for
// "https://www.smithlaw.com/". Anything but an https address prints nothing.
export function websiteLabel(websiteUrl: string | null | undefined): string | null {
  if (!websiteUrl) return null;
  try {
    const url = new URL(websiteUrl);
    if (url.protocol !== 'https:') return null;
    const path = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
    return `${url.host}${path}`;
  } catch {
    return null;
  }
}
